/**
 * Address — finding street addresses in host-written text and turning them
 * into Google Maps links.
 *
 * The Gästemappe names places the way a host writes them: "Hohnsteiner Str.
 * 29, Bad Schandau", "Rudolf-Sendig-Str. 8a · 035022 54610", "Markt 12",
 * "Am Bahnhof 5", "Elbstr. 8, 01814 Rathmannsdorf". A guest should be one tap
 * from the route, so each becomes a link to Google Maps' search — shown
 * exactly as written. A link loads nothing until tapped, so no visitor data
 * goes to Google by merely opening the page.
 *
 * Deliberately conservative, because a false hit sends a guest somewhere odd:
 *   • a street is a word ending in a street word ("…straße", "…str.",
 *     "…weg", "…platz", "Markt" …), a street word after an adjective
 *     ("Hohnsteiner Str."), or a preposition form ("Am Bahnhof",
 *     "An der Elbe") — followed by a house number (8, 8a, 10/11)
 *   • a number followed by a unit ("Am Wochenende 10 Uhr", "Bahnhof 5
 *     Minuten") is not a house number
 *   • a postcode and town right after it are part of the address; a town
 *     after a comma only when it ends there ("…, Bad Schandau ·")
 *   • without a town, the search gets the house's own postcode and town, so
 *     "Markt 12" finds the market square of Bad Schandau, not of anywhere
 *
 * No lookbehind in the patterns: Safari before 16.4 rejects it at parse time.
 */
export class Address
{
    // Endings of a one-word street name ("Basteiplatz", "Rudolf-Sendig-Str.").
    static SUFFIX = /(straße|strasse|str\.|weg|gasse|platz|allee|ring|ufer|markt|damm|chaussee|steig|pfad|graben|promenade|kai)$/i;

    // A street word standing alone after an adjective ("Hohnsteiner Str.").
    static STREET_WORD = /^(straße|strasse|str\.|weg|gasse|platz|allee|ring|ufer|damm|chaussee)$/i;

    // "Am Bahnhof", "An der Elbe", "Auf dem Berg", "Zum Hafen".
    static PREPOSITION = /^(am|an|auf|im|in|zum|zur|hinter|unter|vor|bei|beim)$/i;
    static ARTICLE = /^(der|dem|den|die|das)$/i;

    // "Am Morgen 2 …", "Am Wochenende 10 …": times, never a street.
    static TIME_WORD = /^(morgen|vormittag|mittag|nachmittag|abend|wochenende|tag|anfang|ende|montag|dienstag|mittwoch|donnerstag|freitag|samstag|sonntag|feiertag)$/i;

    // A preposition form is only taken when the number ends the phrase —
    // "Am Bahnhof 5, Bad Schandau", not "Am Kiosk 2 Brötchen".
    static PHRASE_END = /^\s*(?:$|[·,.;:()\/\n|–-])/;

    // Words after a number that make it a quantity, not a house number.
    static UNIT = /^(uhr|min|minuten|std|stunden|h|km|m|meter|€|euro|eur|%|prozent|personen|pers|x|mal|tage|tag|nächte|plätze|stück)(?!\p{L})/iu;

    // Candidate: up to four words (letters, hyphens, a trailing dot), a house
    // number, optionally a letter ("8a") and a range ("10/11", "3-5").
    static CANDIDATE = /((?:[\p{L}][\p{L}.\-]*\s+){0,3}[\p{L}][\p{L}.\-]*)\s+(\d{1,4}(?:\s?[a-zA-Z](?!\p{L}))?(?:\s?[\/\-–]\s?\d{1,4}(?:\s?[a-zA-Z](?!\p{L}))?)?)(?![\d\p{L}])/gu;

    // What may follow the house number as the locality.
    static POSTCODE_TOWN = /^(\s*,?\s*(?:D-)?(\d{5})\s+([\p{Lu}][\p{L}\-]*(?:\s+[\p{Lu}][\p{L}\-]*)?))/u;
    static COMMA_TOWN = /^(\s*,\s*([\p{Lu}][\p{L}\-]*(?:\s+[\p{Lu}][\p{L}\-]*)?))(?=\s*(?:$|[·,.;()\/\n|–-]))/u;

    /**
     * The Google Maps search for an address as written.
     *
     * @param {string} address
     * @param {string} [locality]  "01814 Bad Schandau" — added when the address
     *        has no town of its own.
     */
    static mapsUrl( address, locality = "" )
    {
        const hasTown = /\d{5}|,/.test( address );
        const query = hasTown || !locality ? address : address + ", " + locality;
        return "https://www.google.com/maps/search/?api=1&query=" + encodeURIComponent( query.replace( /\s+/g, " " ).trim() );
    }

    /**
     * The street part of a candidate's words, or null when they are not one:
     * the trailing street-named word ("Postfiliale Basteiplatz" → "Basteiplatz"),
     * adjective + street word ("Hohnsteiner Str."), or preposition (+ article)
     * + name ("Am Bahnhof", "An der Elbe"; `isPhrase`).
     *
     * @returns {({ street: string, isPhrase: boolean }|null)}
     */
    static streetOf( words )
    {
        const last = words[ words.length - 1 ];

        // Preposition forms, searched from the left so "Arzt Am Ring" keeps
        // "Am Ring". The name after them must be capitalised ("Am Morgen" is
        // filtered by the house-number rules, "am vereinbarten" by this).
        for ( let i = 0; i < words.length - 1; i++ )
        {
            if ( !Address.PREPOSITION.test( words[ i ] ) || !/^[\p{Lu}]/u.test( words[ i ] ) )
            {
                continue;
            }
            let j = i + 1;
            if ( Address.ARTICLE.test( words[ j ] ) && j < words.length - 1 )
            {
                j++;
            }
            if ( /^[\p{Lu}]/u.test( words[ j ] ) && j === words.length - 1 && !Address.TIME_WORD.test( words[ j ] ) )
            {
                return { "street" : words.slice( i ).join( " " ), "isPhrase" : true };
            }
        }

        if ( !/^[\p{Lu}]/u.test( last ) )
        {
            return null;
        }
        if ( Address.STREET_WORD.test( last ) )
        {
            const adjective = words[ words.length - 2 ];
            return adjective && /^[\p{Lu}]/u.test( adjective )
                ? { "street" : adjective + " " + last, "isPhrase" : false }
                : null;
        }
        if ( Address.SUFFIX.test( last ) )
        {
            return { "street" : last, "isPhrase" : false };
        }
        return null;
    }

    /**
     * Split a text into plain and address parts, in order:
     * [ { text }, { text, map }, … ].
     *
     * @param {string} text
     * @param {string} [locality]  The house's "postcode town", for addresses
     *        written without one (see mapsUrl).
     */
    static split( text, locality = "" )
    {
        const source = String( text || "" );
        if ( !source )
        {
            return [];
        }

        const hits = [];
        Address.CANDIDATE.lastIndex = 0;
        let m;
        while ( ( m = Address.CANDIDATE.exec( source ) ) !== null )
        {
            const words = m[ 1 ].split( /\s+/ );
            const found = Address.streetOf( words );
            const numberEnd = m.index + m[ 0 ].length;
            const rest = source.slice( numberEnd );

            // "10 Uhr", "5 Minuten": a quantity.
            if ( !found || Address.UNIT.test( rest.trim() ) || ( found.isPhrase && !Address.PHRASE_END.test( rest ) ) )
            {
                // Let the next search start inside this candidate: its last
                // words may begin a real address.
                Address.CANDIDATE.lastIndex = m.index + m[ 1 ].length;
                continue;
            }

            const start = m.index + m[ 1 ].length - found.street.length;
            let end = numberEnd;
            const locality1 = rest.match( Address.POSTCODE_TOWN ) || rest.match( Address.COMMA_TOWN );
            if ( locality1 )
            {
                end += locality1[ 1 ].length;
            }

            const shown = source.slice( start, end );
            hits.push( { start, end, map : Address.mapsUrl( shown, locality ) } );
            Address.CANDIDATE.lastIndex = end;
        }

        if ( hits.length === 0 )
        {
            return [ { text : source } ];
        }

        const parts = [];
        let cursor = 0;
        hits.forEach( ( hit ) =>
        {
            if ( hit.start > cursor )
            {
                parts.push( { text : source.slice( cursor, hit.start ) } );
            }
            parts.push( { text : source.slice( hit.start, hit.end ), map : hit.map } );
            cursor = hit.end;
        } );
        if ( cursor < source.length )
        {
            parts.push( { text : source.slice( cursor ) } );
        }
        return parts;
    }
}
